import { Card, Statistic, Typography } from "antd";
import { ReactNode, useEffect, useState } from "react";
import { PaginationInput } from "../../repository/data.default";
import { QueryCondition } from "../../code/search-query/search-query";
import { DrillLink } from "./DashboardSection";

// A platform-overview stat card: reads a filtered total cheaply via the endpoint's `totalRecords`
// (pageSize 1) so no rows are shipped just to count them.

// A secondary count shown under the headline (e.g. "12 managers"), fetched the same cheap way
// and scoped by the card's conditions.
export interface StatCardSubCount {
  readonly label: string;
  readonly fetch: (
    input: PaginationInput,
  ) => Promise<{ data: any[]; totalRecords: number }>;
}

export interface StatCardProps {
  readonly title: string;
  readonly fetch: (
    input: PaginationInput,
  ) => Promise<{ data: any[]; totalRecords: number }>;
  readonly icon?: ReactNode;
  /** Optional scope conditions (e.g. company equals) to count within. */
  readonly conditions?: QueryCondition[];
  /** Optional breakdown counts rendered as a " · "-joined sub-line under the headline. */
  readonly subCounts?: StatCardSubCount[];
  /** Admin route this count drills through to, so the number opens as the list behind it. */
  readonly drillTo?: string;
  /** Link wording; defaults to "View". */
  readonly drillLabel?: string;
}

function StatCard(props: StatCardProps) {
  const [value, setValue] = useState<number | undefined>(undefined);
  const [subValues, setSubValues] = useState<number[] | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSubValues(undefined);
    const input: PaginationInput = {
      searchQuery: { conditions: props.conditions ?? [] },
      pageNumber: 0,
      pageSize: 1,
    };
    props
      .fetch(input)
      .then((r) => {
        if (!cancelled) {
          setValue(r.totalRecords);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    if (props.subCounts?.length) {
      Promise.all(props.subCounts.map((s) => s.fetch(input)))
        .then((results) => {
          if (!cancelled) setSubValues(results.map((r) => r.totalRecords));
        })
        .catch(() => {
          if (!cancelled) setSubValues(undefined);
        });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(props.conditions)]);

  return (
    <Card className="stat-card flex-auto">
      <Statistic
        title={props.title}
        value={value ?? 0}
        loading={loading}
        prefix={props.icon}
      />
      {props.subCounts && (
        <Typography.Text type="secondary" className="text-xs">
          {subValues
            ? props.subCounts
                .map((s, i) => `${subValues[i]} ${s.label}`)
                .join(" · ")
            : "—"}
        </Typography.Text>
      )}
      <DrillLink to={props.drillTo} label={props.drillLabel} />
    </Card>
  );
}

export default StatCard;
